import { FloatingTooltip, InputGroup, TooltipCore } from '@payaca/ambrosia';
import UntitledIcon from '@payaca/untitled-icons';
import { FC } from 'react';

import PopupBlockedModal from '@/ui/components/clientPortalPreview/PopupBlockedModal';
import UnavailableReasons from '@/ui/components/clientPortalPreview/UnavailableReasons';
import { useClientPortalPreview } from '@/ui/components/clientPortalPreview/useClientPortalPreview';

/**
 * Opens the client portal as the Project's Customer in a new tab. Renders as a
 * segment of an `InputGroup`, so it belongs inside one.
 *
 * Where the preview is unavailable the button is disabled and gives the reasons
 * on hover. It renders nothing at all for a user without the permission, who
 * has nothing to act on.
 *
 * @param projectId the Project's `publicId`
 */
const ClientPortalPreviewButton: FC<{ projectId: string }> = (props) => {
  const { projectId } = props;

  const {
    canPreview,
    openPreview,
    isPending,
    isBlocked,
    unavailableReasons,
    blockedPreviewUrl,
    closeBlockedPreview,
  } = useClientPortalPreview(projectId);

  if (!canPreview) {
    return null;
  }

  return (
    <>
      {/* Renders through a portal, so it does not sit in the group's layout. */}
      <PopupBlockedModal
        url={blockedPreviewUrl}
        onClose={closeBlockedPreview}
      />

      {/* The button is a segment of the group its caller renders, so it cannot
          be wrapped in `Tooltip` — the wrapper would break the group's rounding
          and seams. `FloatingTooltip` anchors to the button itself. */}
      <FloatingTooltip enabled={unavailableReasons.length > 0}>
        <InputGroup.Button
          variant="secondary"
          // Gated with `aria-disabled` rather than the native attribute so the
          // button stays hoverable and focusable, and can say why it is
          // unavailable. Block the click here instead.
          aria-disabled={isBlocked}
          onClick={() => {
            if (!isBlocked) openPreview();
          }}
          isProcessing={isPending}
          // The group sits at the end of a header whose title can be long, so
          // without this the label wraps and leaves the icon stranded beside it.
          className="whitespace-nowrap"
        >
          <UntitledIcon name="eye.3" className="size-4" />
          Preview as customer
          <span className="sr-only"> (opens in a new tab)</span>
        </InputGroup.Button>

        <TooltipCore>
          <UnavailableReasons reasons={unavailableReasons} />
        </TooltipCore>
      </FloatingTooltip>
    </>
  );
};

export default ClientPortalPreviewButton;
